import { Location } from 'history';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { TeamRole } from '../../../../../typings/TeamMember.interface';
import { withStaticProperties } from '../../utils/withStaticProperties';
import { usePermissionAwareDisabled } from '../contexts/TeamPermissionContext';
import { ClickableArea } from './base/Button';
import { StyledCardSection } from './base/Card';
import Icon, { IconName } from './base/Icon';
import Loading from './base/Loading';
import Text from './base/Text';
import Tooltip from './base/Tooltip';
import Dropdown, { DropdownProps } from './Dropdown';
import { Div } from './helpers/StyledUtils';
import Search from './Search';

export interface DropdownMenuOption {
  id?: string;
  label: string;
  description?: string;
  icon?: IconName | React.ReactElement;
  danger?: boolean;
  mono?: boolean;
  disabled?: boolean;
  tooltip?: string;
  selected?: boolean;
  action_icon?: IconName;
  role?: TeamRole;
  block?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  to?: string | Partial<Location> | ((location: Location) => string | Partial<Location>);
}

export interface DropdownMenuProps extends Omit<DropdownProps, 'children'> {
  search?: boolean;
  search_placeholder?: string;
  search_term?: string;
  setSearchTerm?: (value: string) => void;
  getItemsFooter?: (search_term: string) => ReactNode;
  search_is_loading?: boolean;
  options: DropdownMenuOption[];
  search_ref?: React.RefObject<HTMLInputElement | null>;
  options_max_height?: number | { px?: number; vh?: number; pc?: number; raw?: string };
}

const StyledScrollableArea = styled(Div)`
  flex-grow: 1;
  overflow-y: auto;
  box-sizing: border-box;
  flex-grow: 1;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  options,
  search_ref,
  search: show_search,
  search_placeholder,
  search_term = '',
  search_is_loading = false,
  setSearchTerm,
  getItemsFooter,
  options_max_height,
  ...props
}) => {
  const dropdown_props = { ...props };
  const [search, _setSearch] = useState<string>(search_term);

  const setSearch = (value: string) => {
    if (setSearchTerm) {
      setSearchTerm(value);
    }

    _setSearch(value);
  };

  const filtered_options = search
    ? options.filter(
        (option) =>
          option.label.toLowerCase().indexOf(search.toLowerCase()) > -1 ||
          (option.description &&
            option.description.toLowerCase().indexOf(search.toLowerCase()) > -1),
      )
    : options;

  const items_footer = getItemsFooter ? getItemsFooter(search_term) : null;

  return (
    <Dropdown {...dropdown_props}>
      {(toggle) => (
        <>
          {show_search && (
            <StyledCardSection>
              <Search
                borderless
                small
                placeholder={search_placeholder}
                value={search}
                onChange={setSearch}
                search_ref={search_ref}
              />
            </StyledCardSection>
          )}
          {search_is_loading ? (
            <StyledCardSection p={3} flex={{ align: 'center', justify: 'center' }}>
              <Loading />
            </StyledCardSection>
          ) : filtered_options.length > 0 ? (
            <StyledCardSection>
              <StyledScrollableArea max_h={options_max_height || { px: 242 }} p={1}>
                {filtered_options.map((option) => (
                  <DropdownMenuOptionItem
                    key={option.id ?? option.label}
                    toggle={toggle}
                    {...option}
                    disabled={Boolean(option.disabled || dropdown_props.disabled)}
                  />
                ))}
              </StyledScrollableArea>
            </StyledCardSection>
          ) : (
            <StyledCardSection p={{ y: 2.5, x: 3 }}>
              <Text muted>
                <Icon icon="info" left /> {search ? 'No results...' : 'No options...'}
              </Text>
            </StyledCardSection>
          )}
          {items_footer && <StyledCardSection>{items_footer}</StyledCardSection>}
        </>
      )}
    </Dropdown>
  );
};

const StyledDropdownMenuOptionItem = styled.div(
  ({ theme }) => css`
    border-radius: ${theme.radius.normal};
    user-select: none;

    a,
    button {
      border-radius: ${theme.radius.normal};

      &:focus-visible {
        box-shadow: none;
        outline: 2px solid ${theme.colors.outline.focus.primary};
        border-radius: ${theme.radius.small_inset};
        outline-offset: 1px;
      }
    }
  `,
);

export const DropdownMenuOptionItem = ({
  toggle,
  label,
  description,
  selected,
  icon,
  action_icon,
  onClick,
  to,
  tooltip,
  disabled: _disabled,
  danger,
  mono,
  role,
}: DropdownMenuOption & { toggle?: (b: boolean) => void }) => {
  const disabled = usePermissionAwareDisabled(_disabled, role);
  const content = (
    <ClickableArea
      {...(to
        ? { to, as: Link, onClick: () => toggle?.(false) }
        : {
            onClick: (e) => {
              e.preventDefault();
              toggle?.(false);
              onClick && onClick(e);
            },
          })}
      disabled={disabled}
      danger={danger}
      p={{ y: 1.5, x: 2 }}>
      <Div flex={{ align: 'center', gap: 2 }}>
        {icon ? (
          typeof icon === 'string' ? (
            <Icon
              danger={danger}
              muted={!danger && !disabled}
              disabled={disabled}
              icon={icon}
              pointer={!disabled}
            />
          ) : (
            icon
          )
        ) : null}
        <Text disabled={disabled} danger={danger} ellipsis semi={!!description} mono={mono}>
          {label}
        </Text>
        {action_icon ? (
          <Icon
            primary
            icon={action_icon}
            right
            style={{ marginLeft: 'auto' }}
            pointer={!disabled}
          />
        ) : (
          selected && (
            <Icon
              primary
              icon="success_circle"
              right
              style={{ marginLeft: 'auto' }}
              pointer={!disabled}
            />
          )
        )}
      </Div>
      {description && (
        <Text
          neutral={disabled}
          muted={!disabled}
          max_width="256px"
          text_wrap
          p={icon ? { l: 6 } : undefined}>
          {description}
        </Text>
      )}
    </ClickableArea>
  );
  return (
    <StyledDropdownMenuOptionItem>
      {tooltip ? (
        <Tooltip tooltip={tooltip} side="right" fill>
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </StyledDropdownMenuOptionItem>
  );
};

export const DropdownMenuPermission: React.FC<
  React.ComponentPropsWithRef<typeof DropdownMenu> & { role?: TeamRole }
> = ({ role, ...dropdownMenuProps }) => {
  const disabled = usePermissionAwareDisabled(dropdownMenuProps.disabled, role);

  return <DropdownMenu {...dropdownMenuProps} disabled={disabled} />;
};

export default withStaticProperties(DropdownMenu, {
  Permission: DropdownMenuPermission,
});
